import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mic, Volume2, MessageSquare, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function VoiceYourValueGuide() {
  const navigate = useNavigate();

  const speakingOpportunities = [
    "Share an idea in a meeting",
    "Ask a thoughtful question",
    "Offer a different perspective",
    "Give credit to a colleague publicly",
    "Volunteer for a presentation",
    "Email your boss with a suggestion",
    "Provide constructive feedback to a peer",
    "Share your expertise on a topic"
  ];

  const starterPhrases = [
    "I've been thinking about...",
    "Building on what [name] said...",
    "What if we tried...",
    "I noticed that...",
    "From my experience with...",
    "One thing that might help is..."
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mb-4">
            <Mic className="w-8 h-8 text-rose-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Voice Your Value</h1>
          <p className="text-lg text-secondary-600">Speak up and be heard in professional settings</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Speaking Up Matters</h2>
          <p className="text-secondary-700 mb-4">
            Women speak 75% less than men in professional meetings. Yet visibility is crucial for career advancement. When you share your ideas, you demonstrate leadership, build credibility, and ensure your contributions are recognized.
          </p>
          <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
            <p className="text-rose-800 font-medium">
              💪 Your perspective is valuable. The room needs diverse voices. When you stay silent, everyone loses.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Volume2 className="w-6 h-6 text-rose-500" />
            Opportunities to Speak Up
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {speakingOpportunities.map((opp, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-rose-50 rounded-lg">
                <div className="w-2 h-2 bg-rose-500 rounded-full" />
                <span className="text-secondary-700">{opp}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 border-2 border-rose-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-rose-500" />
            Starter Phrases
          </h2>
          <p className="text-secondary-600 mb-4">Not sure how to begin? Try these conversation starters:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {starterPhrases.map((phrase, i) => (
              <div key={i} className="p-3 bg-white rounded-lg">
                <span className="text-secondary-700 italic">"{phrase}"</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-amber-500" />
            Today's Challenge
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <h3 className="font-bold text-amber-800 mb-2">If You Have Meetings Today:</h3>
              <p className="text-amber-900">Share ONE idea, opinion, or question. Set an intention before the meeting to speak at least once.</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <h3 className="font-bold text-blue-800 mb-2">If You Don't Have Meetings:</h3>
              <p className="text-blue-900">Email your boss with a suggestion, reach out to a colleague with helpful feedback, or share an idea in a team chat.</p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Overcoming the Fear</h2>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="text-rose-500 font-bold">1.</span>
              <span className="text-secondary-700"><strong>Prepare:</strong> If you know the meeting topic, prepare one thing to contribute</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-500 font-bold">2.</span>
              <span className="text-secondary-700"><strong>Speak early:</strong> The longer you wait, the harder it gets</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-500 font-bold">3.</span>
              <span className="text-secondary-700"><strong>Use "we" language:</strong> "What if we tried..." feels less risky</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-500 font-bold">4.</span>
              <span className="text-secondary-700"><strong>Remember:</strong> Imperfect contribution beats perfect silence</span>
            </li>
          </ul>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "When did I speak up today? How did it feel before, during, and after? What would I say differently next time?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default VoiceYourValueGuide;
